import { useState, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../emailjs-config';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * useContactForm — encapsulates all Hire Me form logic:
 *   - field state management
 *   - validation
 *   - EmailJS submission
 *   - loading / success / error states
 *
 * Returns everything the Contact component needs to render.
 */
export function useContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!EMAIL_REGEX.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!form.message.trim()) newErrors.message = 'Message is required.';
    return newErrors;
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const timePH = new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' });
      const templateParams = {
        title: 'Hire',
        name: `${form.firstName} ${form.lastName}`.trim(),
        time: timePH,
        message: form.message,
        email: form.email,
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      if (response.status === 200) {
        setStatus('success');
        setForm(INITIAL_FORM);
        setErrors({});
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again later.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const reset = useCallback(() => {
    setForm(INITIAL_FORM);
    setErrors({});
    setStatus('idle');
    setErrorMessage('');
  }, []);

  return {
    form,
    errors,
    status,
    errorMessage,
    updateField,
    handleSubmit,
    reset,
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isError: status === 'error',
  };
}
